// Phase 2.0 Lite — Template Library page (internal).
// Lists internal templates with status/category/tags filters and per-card
// actions (Preview, Duplicate, Use for event, Archive). Reuses existing
// /api/templates endpoints. Admin-only.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Search, Loader2, Archive, Copy, Eye, Send, Tag as TagIcon,
  ChevronLeft, Filter, ExternalLink, Pencil,
} from 'lucide-react';
import GenerateDemoUrlModal from '../components/templates/GenerateDemoUrlModal';
import TemplateLinksPanel from '../components/templates/TemplateLinksPanel';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { id: '',                 label: 'All' },
  { id: 'draft',            label: 'Draft' },
  { id: 'internal_review',  label: 'Internal review' },
  { id: 'approved',         label: 'Approved' },
];

function StatusBadge({ status }) {
  const tone = {
    draft:            'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
    internal_review:  'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    approved:         'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    archived:         'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  }[status] || 'bg-gray-100 text-gray-600';
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${tone}`}
      data-testid={`template-status-${status}`}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

function TemplateCard({
  template,
  onEdit, onPreview, onDuplicate, onUseForEvent, onArchive,
  onGenerateDemo, onDisableDemo,
  busy,
}) {
  const cover = template.preview_image || template.cover_image || '';
  const coverUrl = cover ? (cover.startsWith('/') ? `${API}${cover}` : cover) : null;
  const updated = template.updated_at
    ? new Date(template.updated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : '—';
  const demoLive = !!template.demo_url_enabled;
  return (
    <div
      className="group rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden flex flex-col"
      data-testid={`template-card-${template.id}`}
    >
      <button
        onClick={() => onPreview(template)}
        className="aspect-[4/5] w-full bg-gray-100 dark:bg-gray-800 relative overflow-hidden"
        title="Preview"
        data-testid={`template-card-preview-${template.id}`}
      >
        {coverUrl ? (
          <img
            src={coverUrl}
            alt={template.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300">
            <Sparkles className="w-8 h-8" />
          </div>
        )}
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          <StatusBadge status={template.status || 'draft'} />
          {demoLive && (
            <span
              className="px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider bg-emerald-500 text-white shadow-sm flex items-center gap-1"
              data-testid={`template-demo-live-pill-${template.id}`}
            >
              <ExternalLink className="w-2.5 h-2.5" />
              Demo live
            </span>
          )}
        </div>
      </button>
      <div className="p-3 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate"
              data-testid={`template-name-${template.id}`}>
            {template.name}
          </h3>
        </div>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
          {template.category || 'Uncategorised'} · edited {updated}
        </p>
        {(template.tags || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3" data-testid={`template-tags-${template.id}`}>
            {(template.tags || []).slice(0, 4).map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
              >
                <TagIcon className="w-2 h-2" />
                {t}
              </span>
            ))}
          </div>
        )}
        {/* Phase 2.3 Lite — Template Links panel (Internal preview + Public demo) */}
        <div className="mb-3">
          <TemplateLinksPanel
            template={template}
            onGenerateDemo={onGenerateDemo}
            onDisableDemo={onDisableDemo}
            busy={busy}
          />
        </div>
        <div className="mt-auto pt-2 grid grid-cols-2 gap-1.5">
          <button
            onClick={() => onEdit(template)}
            disabled={busy}
            className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-[11px] font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 col-span-2"
            data-testid={`template-edit-btn-${template.id}`}
          >
            <Pencil className="w-3 h-3" />
            Edit
          </button>
          <button
            onClick={() => onPreview(template)}
            className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-[11px] font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            data-testid={`template-preview-btn-${template.id}`}
          >
            <Eye className="w-3 h-3" />
            Preview
          </button>
          <button
            onClick={() => onUseForEvent(template)}
            disabled={busy}
            className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-[11px] font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
            data-testid={`template-use-btn-${template.id}`}
          >
            <Send className="w-3 h-3" />
            Use for event
          </button>
          <button
            onClick={() => onDuplicate(template)}
            disabled={busy}
            className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-[11px] font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
            data-testid={`template-duplicate-btn-${template.id}`}
          >
            <Copy className="w-3 h-3" />
            Duplicate
          </button>
          <button
            onClick={() => onArchive(template)}
            disabled={busy || template.status === 'archived'}
            className="col-span-2 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-[11px] font-medium text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 disabled:opacity-40"
            data-testid={`template-archive-btn-${template.id}`}
          >
            <Archive className="w-3 h-3" />
            {template.status === 'archived' ? 'Archived' : 'Archive'}
          </button>
        </div>
      </div>
    </div>
  );
}

function UseTemplateForEventModal({ open, template, token, onClose, onApplied }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState('');
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios
      .get(`${API}/api/events`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setEvents(r.data || []);
        if ((r.data || []).length > 0) setSelected(r.data[0].id);
      })
      .catch(() => toast.error('Could not load events'))
      .finally(() => setLoading(false));
  }, [open, token]);

  if (!open || !template) return null;

  const handleApply = async () => {
    if (!selected) return;
    setApplying(true);
    try {
      const r = await axios.post(
        `${API}/api/templates/${template.id}/apply-to-event`,
        { event_id: selected },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Applied "${template.name}"`, {
        description: 'A safety snapshot was created in event History.',
        action: {
          label: 'Open event',
          onClick: () => window.location.assign(`/events/${selected}/invitation-editor`),
        },
      });
      onApplied?.(r.data);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="use-template-modal"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3 border-b border-gray-100 dark:border-gray-800">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Use template for an event
          </h3>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
            <strong>{template.name}</strong> will be copied into the chosen event invitation.
          </p>
        </div>
        <div className="p-5 space-y-3">
          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 px-3 py-2">
            <p className="text-[11px] text-amber-800 dark:text-amber-200 leading-relaxed">
              This will replace the current invitation design for the selected event. A safety
              snapshot named <em>“Before applying template”</em> will be created first, so you
              can roll back from event History.
            </p>
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
              Choose event
            </label>
            {loading ? (
              <div className="flex items-center text-xs text-gray-400">
                <Loader2 className="w-3 h-3 animate-spin mr-1" /> Loading…
              </div>
            ) : (
              <select
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                data-testid="use-template-event-select"
              >
                {events.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.couple_names || e.name || e.slug || e.id}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
        <div className="px-5 py-3.5 border-t border-gray-100 dark:border-gray-800 flex items-center justify-end gap-2 bg-gray-50/50 dark:bg-gray-900/50">
          <button
            onClick={onClose}
            disabled={applying}
            className="px-3.5 py-1.5 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            data-testid="use-template-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={applying || !selected}
            className="px-4 py-1.5 rounded-lg text-xs font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 flex items-center gap-1.5"
            data-testid="use-template-apply-btn"
          >
            {applying ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            Apply to event
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TemplateLibraryPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [useForEventTemplate, setUseForEventTemplate] = useState(null);
  // Phase 2.1 — demo URL modal state
  const [demoModalTemplate, setDemoModalTemplate] = useState(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (includeArchived) params.set('include_archived', '1');
      if (search.trim()) params.set('search', search.trim());
      const r = await axios.get(`${API}/api/templates?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplates(r.data || []);
    } catch {
      toast.error('Could not load templates');
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, includeArchived, search]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handlePreview = (t) => {
    navigate(`/studio/templates/${t.id}/preview`);
  };

  // Phase 2.0.1 — Edit template in Studio.
  const handleEdit = (t) => {
    navigate(`/studio/templates/${t.id}/edit`);
  };

  const handleDuplicate = async (t) => {
    const name = window.prompt(`Duplicate "${t.name}" — new name:`, `${t.name} Copy`);
    if (!name) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/templates/${t.id}/duplicate`,
        { name },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Duplicated as "${r.data.name}"`);
      fetchTemplates();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Duplicate failed');
    } finally {
      setBusy(false);
    }
  };

  const handleArchive = async (t) => {
    if (!window.confirm(`Archive "${t.name}"? It will be hidden from the default list.`)) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/templates/${t.id}/archive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Archived');
      fetchTemplates();
    } catch {
      toast.error('Archive failed');
    } finally {
      setBusy(false);
    }
  };

  // Phase 2.1 — demo URL actions
  const handleGenerateDemo = (t) => {
    if (t.status !== 'approved') {
      toast.error('Approve this template before generating a public demo link.');
      return;
    }
    setDemoModalTemplate(t);
  };

  const handleDisableDemo = async (t) => {
    if (!window.confirm(
      `Disable the public demo link for "${t.name}"?\n` +
      `Anyone with the link will no longer be able to view it.`
    )) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/templates/${t.id}/demo/disable`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Demo link disabled');
      fetchTemplates();
    } catch {
      toast.error('Could not disable demo');
    } finally {
      setBusy(false);
    }
  };

  const sorted = useMemo(() => {
    // Sort: non-archived first, then by updated_at desc
    return [...templates].sort((a, b) => {
      const aArch = a.status === 'archived';
      const bArch = b.status === 'archived';
      if (aArch !== bArch) return aArch ? 1 : -1;
      return (b.updated_at || '').localeCompare(a.updated_at || '');
    });
  }, [templates]);

  return (
    <div
      className="min-h-screen bg-gray-50 dark:bg-gray-950"
      data-testid="template-library-page"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            data-testid="template-library-back-btn"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Template Library
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Internal designs you can reuse across events. Editing a template here doesn&apos;t affect
              events that already used it.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mb-5">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              data-testid="template-library-search"
            />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Filter className="w-3.5 h-3.5 text-gray-400 ml-1" />
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                onClick={() => setStatusFilter(opt.id)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium ${
                  statusFilter === opt.id
                    ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300'
                    : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
                }`}
                data-testid={`template-library-filter-${opt.id || 'all'}`}
              >
                {opt.label}
              </button>
            ))}
            <label
              className="flex items-center gap-1.5 ml-2 text-[11px] text-gray-600 dark:text-gray-300 cursor-pointer select-none"
              data-testid="template-library-include-archived-label"
            >
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
                className="accent-violet-600"
                data-testid="template-library-include-archived"
              />
              Show archived
            </label>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading…
          </div>
        ) : sorted.length === 0 ? (
          <div
            className="text-center py-20 text-gray-400"
            data-testid="template-library-empty"
          >
            <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No templates yet.</p>
            <p className="text-[11px] mt-1">
              Save a design from a Studio event with “Save as Template”.
            </p>
          </div>
        ) : (
          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4"
            data-testid="template-library-grid"
          >
            {sorted.map((t) => (
              <TemplateCard
                key={t.id}
                template={t}
                onEdit={handleEdit}
                onPreview={handlePreview}
                onDuplicate={handleDuplicate}
                onUseForEvent={(tt) => setUseForEventTemplate(tt)}
                onArchive={handleArchive}
                onGenerateDemo={handleGenerateDemo}
                onDisableDemo={handleDisableDemo}
                busy={busy}
              />
            ))}
          </div>
        )}
      </div>

      <UseTemplateForEventModal
        open={!!useForEventTemplate}
        template={useForEventTemplate}
        token={token}
        onClose={() => setUseForEventTemplate(null)}
        onApplied={() => fetchTemplates()}
      />

      <GenerateDemoUrlModal
        open={!!demoModalTemplate}
        template={demoModalTemplate}
        token={token}
        onClose={() => setDemoModalTemplate(null)}
        onGenerated={() => fetchTemplates()}
      />
    </div>
  );
}
